import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Clock,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Brain,
  BookOpen,
  Target,
  Loader2,
  Home,
  Play,
  XCircle,
  TrendingUp,
  TrendingDown,
  RotateCcw
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_academia-sabeneval/artifacts/ai0i1vi4_2F29CCC3-E812-45AF-8948-674464C4314C.PNG";

const AREA_ICONS = {
  pensamiento_matematico: Brain,
  comprension_lectora: BookOpen,
  redaccion_indirecta: Target
};

const AREA_NAMES = {
  pensamiento_matematico: "Pensamiento Matemático",
  comprension_lectora: "Comprensión Lectora",
  redaccion_indirecta: "Redacción Indirecta"
};

const LEVEL_CONFIG = {
  bajo: { color: "text-red-600", bgColor: "bg-red-100", label: "Nivel Bajo", emoji: "🔴" },
  intermedio: { color: "text-yellow-600", bgColor: "bg-yellow-100", label: "Nivel Intermedio", emoji: "🟡" },
  alto: { color: "text-green-600", bgColor: "bg-green-100", label: "Nivel Alto", emoji: "🟢" }
};

export default function SimuladorPage() {
  const { simulatorId } = useParams();
  const navigate = useNavigate();
  const [simulator, setSimulator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [examStarted, setExamStarted] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Check access
  useEffect(() => {
    const savedEmail = sessionStorage.getItem("studentEmail");
    if (!savedEmail) {
      navigate("/mi-curso");
      return;
    }
    fetchSimulator();
  }, [simulatorId]);

  const fetchSimulator = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/simulators/${simulatorId}`);
      setSimulator(response.data);
      setQuestions(response.data.questions || []);
      setTimeRemaining(response.data.time_minutes * 60);
    } catch (error) {
      toast.error("Error al cargar simulador");
      navigate("/mi-curso/modulo/6");
    } finally {
      setLoading(false);
    }
  };

  // Timer
  useEffect(() => {
    if (!examStarted || timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [examStarted, timeRemaining]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const startExam = () => {
    setExamStarted(true);
    setStartTime(Date.now());
  };

  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);

    const timeTaken = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;
    const studentEmail = sessionStorage.getItem("studentEmail") || "";

    const submission = {
      answers: Object.entries(answers).map(([questionId, selectedOptionId]) => ({
        question_id: questionId,
        selected_option_id: selectedOptionId
      })),
      time_taken_seconds: timeTaken
    };

    try {
      const response = await axios.post(`${API}/simulators/${simulatorId}/submit?student_email=${studentEmail}`, submission);
      setResult(response.data);
      setExamStarted(false);
    } catch (error) {
      toast.error("Error al enviar simulador");
      setSubmitting(false);
    }
  }, [answers, startTime, submitting, simulatorId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  // Result Screen
  if (result) {
    const levelConfig = LEVEL_CONFIG[result.level] || LEVEL_CONFIG.bajo;

    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b border-slate-100 py-4">
          <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
            <a href="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Sabeneval" className="h-10 w-auto" />
              <span className="font-heading font-bold text-lg text-purple-700 hidden sm:block">SABENEVAL</span>
            </a>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 py-8">
          <Card className="shadow-lg mb-8">
            <div className={`${levelConfig.bgColor} px-6 py-4 border-b`}>
              <div className="flex items-center justify-center gap-3">
                <h2 className={`text-xl font-semibold ${levelConfig.color}`}>
                  {levelConfig.emoji} {levelConfig.label}
                </h2>
              </div>
            </div>
            <CardContent className="p-8">
              <h1 className="font-heading text-2xl font-bold text-slate-900 text-center mb-2">
                Resultado del Simulador
              </h1>
              <p className="text-slate-600 text-center mb-8">{simulator?.title}</p>

              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-purple-600">{result.total_score}</p>
                  <p className="text-sm text-slate-500">Aciertos</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-slate-700">{result.total_questions}</p>
                  <p className="text-sm text-slate-500">Preguntas</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-teal-600">{result.percentage}%</p>
                  <p className="text-sm text-slate-500">Porcentaje</p>
                </div>
              </div>

              {/* Area Results */}
              <div className="space-y-4 mb-8">
                <h3 className="font-semibold text-slate-900">Resultados por Área</h3>
                {result.areas.map((area) => {
                  const Icon = AREA_ICONS[area.area] || Brain;
                  const isStrong = area.percentage >= 60;
                  return (
                    <div key={area.area}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-5 h-5 ${isStrong ? "text-green-600" : "text-red-600"}`} />
                          <span className="font-medium">{area.area_name}</span>
                        </div>
                        <span className={`font-semibold ${isStrong ? "text-green-600" : "text-red-600"}`}>
                          {area.percentage}%
                        </span>
                      </div>
                      <Progress value={area.percentage} className={`h-2 ${isStrong ? "[&>div]:bg-green-500" : "[&>div]:bg-red-500"}`} />
                    </div>
                  );
                })}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={() => navigate("/mi-curso/modulo/6")} variant="outline" className="rounded-full">
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Volver a Simuladores
                </Button>
                <Button onClick={() => { setResult(null); setAnswers({}); setExamStarted(false); fetchSimulator(); }} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full">
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Intentar de Nuevo
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Intro Screen
  if (!examStarted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-slate-50">
        <header className="bg-white/80 backdrop-blur-md border-b border-slate-100 py-4">
          <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
            <a href="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Sabeneval" className="h-10 w-auto" />
              <span className="font-heading font-bold text-lg text-purple-700 hidden sm:block">SABENEVAL</span>
            </a>
            <Button onClick={() => navigate("/mi-curso/modulo/6")} variant="ghost" className="text-slate-600">
              <ChevronLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
          </div>
        </header>

        <div className="max-w-2xl mx-auto px-4 py-12">
          <Card className="shadow-xl border-teal-100">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Play className="w-10 h-10 text-teal-600" />
              </div>

              <Badge className="bg-teal-100 text-teal-700 mb-4">Simulador EXANI II</Badge>

              <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                {simulator?.title}
              </h1>

              <p className="text-slate-600 mb-6">{simulator?.description}</p>

              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-teal-600">{questions.length}</p>
                  <p className="text-sm text-slate-500">Preguntas</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-teal-600">{simulator?.time_minutes}</p>
                  <p className="text-sm text-slate-500">Minutos</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-teal-600">3</p>
                  <p className="text-sm text-slate-500">Áreas</p>
                </div>
              </div>

              <Button onClick={startExam} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full h-12 px-10 text-base font-medium">
                Comenzar Simulador
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Exam Screen
  const currentQuestion = questions[currentIndex];
  const progress = questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0;
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-100 py-4 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Sabeneval" className="h-8 md:h-10 w-auto" />
            </a>

            <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
              timeRemaining < 300 ? "bg-red-100 text-red-700" : "bg-teal-100 text-teal-700"
            }`}>
              <Clock className={`w-5 h-5 ${timeRemaining < 300 ? "animate-pulse" : ""}`} />
              <span className="font-mono font-semibold">{formatTime(timeRemaining)}</span>
            </div>

            <div className="text-sm text-slate-500">
              <span className="font-medium text-teal-600">{answeredCount}</span> / {questions.length}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <div className="mb-6">
              <div className="flex items-center justify-between text-sm text-slate-500 mb-2">
                <span>Pregunta {currentIndex + 1} de {questions.length}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            {currentQuestion && (
              <Card className="shadow-sm">
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-center gap-2 mb-4">
                    {(() => {
                      const Icon = AREA_ICONS[currentQuestion.area] || Brain;
                      return <Icon className="w-4 h-4 text-teal-600" />;
                    })()}
                    <span className="text-sm font-medium text-teal-600">
                      {AREA_NAMES[currentQuestion.area] || currentQuestion.area}
                    </span>
                  </div>

                  <h2 className="text-lg md:text-xl font-medium text-slate-900 mb-6">
                    {currentQuestion.text}
                  </h2>

                  <RadioGroup
                    value={answers[currentQuestion.id] || ""}
                    onValueChange={(value) => setAnswers({ ...answers, [currentQuestion.id]: value })}
                    className="space-y-3"
                  >
                    {currentQuestion.options.map((option) => (
                      <div key={option.id}>
                        <Label
                          htmlFor={`${currentQuestion.id}-${option.id}`}
                          className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all ${
                            answers[currentQuestion.id] === option.id
                              ? "border-teal-500 bg-teal-50"
                              : "border-slate-200 hover:border-teal-200"
                          }`}
                        >
                          <RadioGroupItem value={option.id} id={`${currentQuestion.id}-${option.id}`} className="text-teal-600" />
                          <span className="text-slate-700">{option.text}</span>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </CardContent>
              </Card>
            )}

            <div className="flex items-center justify-between mt-6">
              <Button onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))} disabled={currentIndex === 0} variant="outline" className="rounded-full">
                <ChevronLeft className="w-4 h-4 mr-1" />
                Anterior
              </Button>

              {currentIndex < questions.length - 1 ? (
                <Button onClick={() => setCurrentIndex(currentIndex + 1)} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full">
                  Siguiente
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={submitting} className="bg-green-600 hover:bg-green-700 text-white rounded-full">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  {submitting ? "Enviando..." : "Finalizar"}
                </Button>
              )}
            </div>
          </div>

          <div className="hidden md:block">
            <Card className="sticky top-24">
              <CardContent className="p-4">
                <h3 className="font-semibold text-slate-900 mb-4">Navegación</h3>
                <div className="grid grid-cols-5 gap-2">
                  {questions.map((q, index) => (
                    <button
                      key={q.id}
                      onClick={() => setCurrentIndex(index)}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                        index === currentIndex
                          ? "bg-teal-600 text-white"
                          : answers[q.id]
                          ? "bg-green-100 text-green-700"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>

                {answeredCount < questions.length && (
                  <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5" />
                      <p className="text-xs text-yellow-700">
                        {questions.length - answeredCount} pregunta(s) sin responder
                      </p>
                    </div>
                  </div>
                )}

                <Button onClick={handleSubmit} disabled={submitting} className="w-full mt-4 bg-teal-600 hover:bg-teal-700 text-white">
                  {submitting ? "Enviando..." : "Finalizar Simulador"}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
